/**
 * HARDCODED (docs/event-experiences.md, "What Matters 2026: hardcoded for
 * now"). The microsite's own config, ported verbatim. On the event page the
 * facts that live in MP win over these: dates, registration window, venue,
 * schedule, topics and capacity come from the details payload, and `facts()`
 * in ./facts.ts says which. What only lives here: the keynote's bio and books,
 * speaker taglines, lunch photos and caterer, session posters, recap words and
 * the next-edition teaser.
 *
 * Art paths stay as the microsite wrote them ('/art/...'); art() resolves them
 * against wherever the widget bundle was served from.
 */
import type { ConferenceConfig } from './lib/conference';

/**
 * The 2026 What Matters Women's Conference, as it exists in MP today.
 *
 * Every ID in this file was read from the live database on 2026-08-17
 * (event 329668, product 525, form 2565). The agenda is the one drafted
 * from the event description and is flagged `agendaIsDraft` until the
 * team's real run-of-show lands in Agenda_Elements.
 */
export const whatMatters: ConferenceConfig = {
  name: 'What Matters',
  ministryMark: 'Woodside Women',
  tagline: 'A weekend to pause, worship, and ask the question together.',
  // Taken from the event's own page and cut to the weekend itself. What came
  // out: the door times (the agenda says it better), the early-bird deadline and
  // refund policy (registration's job), and the six breakout titles (the
  // breakouts board lists them with their speakers and how full they are).
  invitation: 'In a season of chaos and distraction, we pause to ask the question together.',
  description:
    'Join the women of Woodside as we gather at the Troy campus for a weekend of worship, laughter, fellowship, and solid Biblical teaching. Elizabeth Woodson teaches the main sessions and gifted women from campuses all over Woodside will have breakouts in between. Come as you are, with whatever you are carrying. There is a seat here for you.',
  church: 'Woodside Bible Church',
  churchUrl: 'https://woodsidebible.org',
  ministryUrl: 'https://woodsidebible.org/womens-ministry/',

  eventId: 329668,
  productId: 525,
  registrationFormId: 2565,
  basePrice: 125,
  formFields: { campus: 28098, heardAbout: 28101, lunch: 28123 },
  partyScopedFieldIds: [28101],
  breakoutGroups: [
    { slot: 1, groupId: 730, label: 'Breakout Session One', agendaHint: 'Saturday morning' },
    { slot: 2, groupId: 748, label: 'Breakout Session Two', agendaHint: 'Saturday afternoon' },
  ],
  // 1704 = the "I Understand" registration disclaimer pseudo-option.
  hiddenOptionPriceIds: [1704],

  timezone: 'America/Detroit',
  days: [
    { key: 'friday', label: 'Friday', date: '2026-11-06' },
    { key: 'saturday', label: 'Saturday', date: '2026-11-07' },
  ],
  registrationOpens: '2026-05-10T00:00:00-04:00',
  registrationCloses: '2026-10-11T23:59:00-04:00',
  eventStarts: '2026-11-06T19:00:00-05:00',
  eventEnds: '2026-11-07T16:00:00-05:00',

  venue: {
    name: 'Woodside Bible Church, Troy Campus',
    address: '6600 Rochester Rd',
    city: 'Troy, MI',
    mapUrl: 'https://maps.google.com/?q=Woodside+Bible+Church+Troy+MI',
    lat: 42.61615,
    lng: -83.12508,
  },

  // Misty Ledger, Women's Ministry. See ConferenceConfig.host for why this
  // is a literal and not the event's MP contact.
  host: { contactId: 230869, name: 'Misty Legler', role: "Woodside Women's Ministry" },

  keynoteKey: 'elizabeth-woodson',
  speakers: [
    {
      key: 'elizabeth-woodson',
      name: 'Elizabeth Woodson',
      tagline: 'Bible teacher and author',
      external: true,
      // Press photo from elizabethwoodson.net, found by Colton 2026-08-17.
      imageSrc: '/art/elizabeth-woodson-face.jpg',
      cardImage: '/art/elizabeth-woodson.jpg',
      url: 'https://www.elizabethwoodson.net/',
      bio: 'She equips people to understand the big story of the Bible and live it out in everyday life. She founded The Woodson Institute and teaches at conferences and churches across the country.',
      background:
        "She holds a master's in Christian education from Dallas Theological Seminary and has been teaching for more than fifteen years, with a gift for making theology land in ordinary life. She writes for Christianity Today, The Gospel Coalition, and Lifeway Women, teaches in the Lifeway Women Academy, and co-hosts the Shalomies podcast.",
      // Covers and links from elizabethwoodson.net, pulled 2026-08-17.
      books: [
        {
          title: 'Habits of Resistance',
          subtitle: 'Gospel practices for pushing back on the way culture forms us',
          cover: '/art/books/habits-of-resistance-cover.png',
          bleed: true,
          url: 'https://www.lifeway.com/en/product/habits-of-resistance-P005849436',
        },
        {
          title: 'Live Free',
          subtitle: 'A study of Galatians',
          cover: '/art/books/live-free.png',
          url: 'https://www.elizabethwoodson.net/livefree',
        },
        {
          title: 'From Beginning to Forever',
          subtitle: 'A study of the grand narrative of Scripture',
          cover: '/art/books/from-beginning-to-forever.png',
          url: 'https://www.elizabethwoodson.net/from-beginning-to-forever',
        },
        {
          title: 'Embrace Your Life',
          subtitle: 'Finding joy when the life you have is not the life you hoped for',
          cover: '/art/books/embrace-your-life.png',
          url: 'https://www.elizabethwoodson.net/embrace-your-life',
        },
      ],
    },
    {
      key: 'cristina-broomfield',
      name: 'Cristina Broomfield',
      tagline: 'Woodside Bible Church',
      contactId: 230293,
    },
    {
      key: 'lauren-frith',
      name: 'Lauren Frith',
      tagline: 'Woodside Bible Church',
      contactId: 284714,
    },
    {
      key: 'amy-catanzaro',
      name: 'Amy Catanzaro',
      tagline: 'Woodside Bible Church',
      contactId: 417118,
    },
    {
      key: 'maggie-fisk',
      name: 'Maggie Fisk',
      tagline: 'Woodside Bible Church',
      contactId: 328870,
    },
    {
      key: 'tiffani-zale',
      name: 'Tiffani Zale',
      tagline: 'Panel host, Woodside Bible Church',
      contactId: 288063,
    },
    {
      key: 'nancy-stewart',
      name: 'Nancy Stewart',
      tagline: 'Woodside Bible Church',
      contactId: 247421,
    },
  ],

  topics: [
    {
      slug: 'sacred-stewardship',
      title: 'Sacred Stewardship: Honoring God Through Setting Personal Boundaries',
      shortTitle: 'Sacred Stewardship',
      subtitle: 'Honoring God Through Setting Personal Boundaries',
      speakerKey: 'cristina-broomfield',
      blurb:
        'Boundaries are not walls; they are stewardship of the life God gave you. A session on honoring Him with your yes and your no.',
      room: 'Room 201',
      slots: [
        { slot: 1, optionPriceId: 1642, capacity: 200, groupId: 24823 },
        { slot: 2, optionPriceId: 1686, capacity: 200, groupId: 24837 },
      ],
      replay: {
        durationMinutes: 52,
        points: [
          'A boundary is not a wall you build against people. It is a fence around the thing God actually asked you to tend.',
          'Guilt is not a reliable signal. Ask what you were given to carry, not who will be disappointed.',
          'Jesus said no. He walked away from crowds who still wanted something from Him, because His yes belonged somewhere else.',
          'Pick one recurring yes you dread, and name out loud what it costs the people at your own table.',
          'A boundary held kindly once teaches more than a boundary explained ten times.',
        ],
        scripture: ['Mark 1:35-38', 'Galatians 6:4-5', 'Proverbs 4:23'],
      },
    },
    {
      slug: 'women-and-work',
      title: 'Women and Work: Embracing What We Do as an Overflow of Who We Are',
      shortTitle: 'Women and Work',
      subtitle: 'Embracing What We Do as an Overflow of Who We Are',
      speakerKey: 'lauren-frith',
      blurb:
        'Whether your work is an office, a home, or both, it flows out of identity. A session on working from who you are in Christ.',
      room: 'Room 203',
      slots: [
        { slot: 1, optionPriceId: 1643, capacity: 200, groupId: 24824 },
        { slot: 2, optionPriceId: 1687, capacity: 200, groupId: 24838 },
      ],
      replay: {
        durationMinutes: 47,
        points: [
          'Your work is not your worth, and it is also not beneath your worth. Both errors end in the same exhaustion.',
          'A spreadsheet, a sink, a classroom: the work changes and the audience does not.',
          'Ambition is not the problem. Ambition detached from love is.',
          'Ask of any role you are considering: is this shaping me into someone my people can live with?',
          'Rest is not the reward for finishing. It is evidence you believe God keeps working while you sleep.',
        ],
        scripture: ['Colossians 3:23-24', 'Genesis 2:15', 'Psalm 127:1-2'],
      },
    },
    {
      slug: 'death-of-a-dream',
      title: 'Death of a Dream: Trusting God When He Asks You to Place Your Dream on the Altar',
      shortTitle: 'Death of a Dream',
      subtitle: 'Trusting God When He Asks You to Place Your Dream on the Altar',
      speakerKey: 'amy-catanzaro',
      blurb:
        'What do you do when the thing you hoped for does not happen? A session on trusting God with surrendered dreams.',
      room: 'Chapel',
      slots: [
        { slot: 1, optionPriceId: 1646, capacity: 200, groupId: 24827 },
        { slot: 2, optionPriceId: 1690, capacity: 200, groupId: 24840 },
      ],
      replay: {
        durationMinutes: 55,
        points: [
          'Grief over a dream is real grief. Do not let anyone hurry you past it with a verse.',
          'Saying the dream out loud is not unbelief. It is how you hand it over.',
          'God is not asking you to pretend it did not matter. He is asking to be trusted with the thing that did.',
          'Some altars turn out to be places of provision. You rarely know yet which kind you are standing on.',
          'Name one thing the waiting has given you that you would never have gone looking for.',
        ],
        scripture: ['Genesis 22:1-14', 'Psalm 62:8', 'Romans 8:28'],
      },
    },
    {
      slug: 'heart-of-discipleship',
      title: 'The Heart of Discipleship',
      shortTitle: 'The Heart of Discipleship',
      speakerKey: 'maggie-fisk',
      blurb:
        'Following Jesus was never meant to be done alone. A session on what it looks like to be discipled and to disciple others.',
      room: 'Student Center',
      slots: [
        { slot: 1, optionPriceId: 1645, capacity: 200, groupId: 24826 },
        { slot: 2, optionPriceId: 1689, capacity: 200, groupId: 24839 },
      ],
      replay: {
        durationMinutes: 44,
        points: [
          'Discipleship is not a curriculum. It is a life somebody is close enough to watch.',
          'You do not have to be finished to be useful. You have to be honest and one step ahead.',
          'Ask one woman to breakfast. That is the entire strategy.',
          'Being discipled means letting somebody see the parts you manage in public.',
          'It is slow on purpose. Jesus had twelve, and one of them left.',
        ],
        scripture: ['2 Timothy 2:2', 'Mark 3:14', '1 Thessalonians 2:8'],
      },
    },
    {
      slug: 'persevering-with-hope',
      title:
        'Persevering with Hope: Surviving Difficult Seasons and Finding Healing and Wholeness on the Other Side',
      shortTitle: 'Persevering with Hope',
      subtitle: 'Surviving Difficult Seasons and Finding Healing and Wholeness on the Other Side',
      speakerKey: 'tiffani-zale',
      panel: true,
      blurb:
        'A panel of Woodside women share honestly about hard seasons: what carried them, what healed, and where hope showed up.',
      room: 'Worship Center',
      slots: [
        { slot: 1, optionPriceId: 1644, capacity: 300, groupId: 24825 },
        { slot: 2, optionPriceId: 1688, capacity: 300, groupId: 24842 },
      ],
      replay: {
        durationMinutes: 61,
        points: [
          'Every woman on the panel said the same thing about her hardest year: somebody showed up with food and then stayed.',
          'Hope is not optimism. Optimism expects a better outcome; hope expects a faithful God.',
          'Three of the four named the same turning point, and it was telling one safe person the truth.',
          'Healing was not linear for any of them, and none of them would call it finished.',
          'What you survive becomes the thing the next woman borrows courage from.',
        ],
        scripture: ['2 Corinthians 1:3-7', 'Lamentations 3:21-23'],
      },
    },
    {
      slug: 'grace-in-motion',
      title: "Grace in Motion: God's Design for Spiritual Gifts",
      shortTitle: 'Grace in Motion',
      subtitle: "God's Design for Spiritual Gifts",
      speakerKey: 'nancy-stewart',
      blurb:
        'Every believer is gifted on purpose, for others. A session on discovering your spiritual gifts and putting them in motion.',
      room: 'Community Room',
      slots: [
        { slot: 1, optionPriceId: 1641, capacity: 200, groupId: 24822 },
        { slot: 2, optionPriceId: 1685, capacity: 200, groupId: 24836 },
      ],
      replay: {
        durationMinutes: 49,
        points: [
          'You were gifted on purpose, and the purpose was never you.',
          'The test of a gift is not how it makes you feel. It is whether the room is better afterward.',
          'If you do not know your gift, ask three people who have watched you serve. They usually know.',
          'A gift you refuse to use does not stay neutral. The body goes without it.',
          'Start where you already are: the nursery, the parking lot, the group text.',
        ],
        scripture: ['1 Corinthians 12:4-7', 'Romans 12:6-8', '1 Peter 4:10'],
      },
    },
  ],

  // Panera publishes brand assets for media and public use, so the picker shows
  // the actual item instead of a drawing of one. Files live in public/art/lunch/;
  // any that are missing fall back to the pen marks. Note the split source: the
  // press-room image bank has the wordmark but only the retail promo lineup, so
  // the five item photos come from Panera's own catering menu, which is the
  // only place the boxed-lunch deli items are pictured. See the README there.
  caterer: {
    name: 'Panera Bread',
    logo: '/art/lunch/panera.svg',
    assetsFrom: 'https://www.panerabread.com/en-us/press/press-room.html',
  },

  // The details are the event page's own menu copy, so the site says exactly
  // what the registration form says is in the box.
  lunchOptions: [
    {
      value: 'Regular - Turkey & Cheddar',
      photo: '/art/lunch/turkey-cheddar.jpg',
      label: 'Turkey & Cheddar sandwich',
      detail: 'Chips, pickle spear, chocolate chip cookie',
      mark: 'sandwich',
    },
    {
      value: 'Regular - Ham & Cheddar',
      photo: '/art/lunch/ham-cheddar.jpg',
      label: 'Ham & Cheddar sandwich',
      detail: 'Chips, pickle spear, chocolate chip cookie',
      mark: 'sandwich',
    },
    {
      value: 'Vegetarian - Mediterranean Veggie',
      photo: '/art/lunch/mediterranean-veggie.jpg',
      label: 'Mediterranean Veggie sandwich',
      detail: 'Chips, pickle spear, chocolate chip cookie',
      tags: ['Vegetarian'],
      mark: 'sandwich',
    },
    {
      value: 'Vegetarian/Gluten Friendly',
      photo: '/art/lunch/greek-salad.jpg',
      label: 'Greek salad',
      detail: 'Chips and an apple',
      tags: ['Vegetarian', 'Gluten friendly'],
      mark: 'salad',
    },
    {
      value: 'Gluten-Friendly/Conscious - Caesar Salad w/ Chicken',
      photo: '/art/lunch/caesar-chicken.jpg',
      label: 'Caesar salad with chicken',
      detail: 'No croutons, chips and an apple',
      tags: ['Gluten friendly'],
      mark: 'salad',
    },
    {
      value: 'Severe Food Allergy/Allergies: Alternative Option Needed: Please Contact Me',
      label: 'Severe food allergy',
      // No longer a promise to ring her later: choosing this asks what to avoid
      // on the spot. See DetailsStep.
      detail: 'Tell us what to avoid',
      tags: ['Allergy'],
      mark: 'care',
    },
  ],

  // Drafted from the event description: doors 5:30 Friday with the session at
  // 7, doors 8 Saturday with sessions from 9, boxed lunch provided, done by 4.
  // Breakout times and rooms are placeholders until the run-of-show is real.
  agendaIsDraft: true,
  agenda: [
    {
      day: 'friday',
      sequence: 1,
      title: 'Doors open',
      description: 'Check in, find your seat, say hello.',
      start: '17:30',
      end: '19:00',
      kind: 'doors',
      room: 'Main Lobby',
    },
    {
      day: 'friday',
      sequence: 2,
      title: 'Worship and welcome',
      start: '19:00',
      end: '19:35',
      kind: 'worship',
      room: 'Worship Center',
    },
    {
      day: 'friday',
      sequence: 3,
      // PLACEHOLDER TITLES. The three main-stage talks carry topics rather than
      // "Session One with Elizabeth Woodson": a number and a speaker's name tell
      // a woman nothing about what she is going to hear, and her face is already
      // on the card. Which session it was is drawn as a numeral on the card
      // instead, so the ordinal costs no words. The arc is diagnosis, then
      // grounding, then sending. Elizabeth's real titles replace these the
      // moment the team has them.
      title: 'What Has Your Attention',
      description: 'Opening the question: What Matters?',
      start: '19:35',
      end: '20:45',
      kind: 'session',
      room: 'Worship Center',
      speakerKey: 'elizabeth-woodson',
      replay: {
        durationMinutes: 68,
        posterSrc: '/art/sessions/what-has-your-attention.jpg',
        points: [
          'The question is not what should matter. You already know that. The question is what actually has your Tuesday.',
          'Scripture never asks you to care about less. It asks you to be ordered rightly.',
          'Whatever you protect when you are exhausted is your real answer.',
          'Mary was not praised for doing nothing. She was praised for choosing the one thing nobody could take from her.',
        ],
        scripture: ['Luke 10:38-42', 'Psalm 90:12'],
      },
    },
    {
      day: 'friday',
      sequence: 4,
      title: 'Evening close',
      start: '20:45',
      end: '21:00',
      kind: 'close',
      room: 'Worship Center',
    },
    {
      day: 'saturday',
      sequence: 1,
      title: 'Doors open, coffee and pastries',
      start: '08:00',
      end: '09:00',
      kind: 'doors',
      room: 'Main Lobby',
    },
    {
      day: 'saturday',
      sequence: 2,
      title: 'Grounded in Christ',
      // Worship moves out of the title and into the description: it is what
      // happens in the room before the talk, not what the talk is called.
      description: 'Worship first, then teaching.',
      start: '09:00',
      end: '10:25',
      kind: 'session',
      room: 'Worship Center',
      speakerKey: 'elizabeth-woodson',
      replay: {
        durationMinutes: 74,
        posterSrc: '/art/sessions/grounded-in-christ.jpg',
        points: [
          'You cannot think your way into loving God. You practice your way in.',
          'The people in Scripture who knew Him best were the ones who kept showing up when nothing was happening.',
          'Distraction is not a character flaw in you. It is a business model aimed at you, and naming it that way changes how you fight it.',
          'Pick one habit and keep it badly for a year. That beats keeping four of them for a month.',
        ],
        scripture: ['Deuteronomy 6:4-9', 'Colossians 3:1-4'],
      },
    },
    {
      day: 'saturday',
      sequence: 3,
      title: 'Breakout Session One',
      description: 'Your first chosen breakout.',
      start: '10:45',
      end: '11:45',
      kind: 'breakout',
      slot: 1,
    },
    {
      day: 'saturday',
      sequence: 4,
      title: 'Lunch',
      description: 'Your lunch choice from registration, plus snacks through the day.',
      start: '12:00',
      end: '13:00',
      kind: 'meal',
      room: 'Atrium',
    },
    {
      day: 'saturday',
      sequence: 5,
      title: 'Breakout Session Two',
      description: 'Your second chosen breakout.',
      start: '13:15',
      end: '14:15',
      kind: 'breakout',
      slot: 2,
    },
    {
      day: 'saturday',
      sequence: 6,
      title: 'Carrying It Home',
      description: 'Closing session and worship.',
      start: '14:35',
      end: '15:50',
      kind: 'session',
      room: 'Worship Center',
      speakerKey: 'elizabeth-woodson',
      replay: {
        durationMinutes: 71,
        posterSrc: '/art/sessions/carrying-it-home.jpg',
        points: [
          'Nothing you decide this weekend survives Monday unless somebody else knows about it.',
          'Go home and change one thing. One. Then tell the woman you rode here with.',
          'You were never meant to carry your own faith by yourself, and you are sitting in a room full of the answer.',
          'What matters is not a list. It is a Person, and He already knows your name.',
        ],
        scripture: ['Hebrews 10:23-25', 'Philippians 1:6'],
      },
    },
    {
      day: 'saturday',
      sequence: 7,
      title: 'Send off',
      start: '15:50',
      end: '16:00',
      kind: 'close',
      room: 'Worship Center',
    },
  ],

  /**
   * The weekend, remembered.
   *
   * PLACEHOLDER PHOTOGRAPHY. Every `src` here is an Unsplash frame standing in
   * for a picture the team has not taken yet (the conference is in November).
   * They are in the config rather than the component for exactly that reason:
   * swapping the whole gallery for the real weekend is editing this list, or
   * pasting URLs into the admin page, and never a code change.
   *
   * The captions are written the way the team would write them — a time and a
   * fact — because a photo grid with no words is an album, and a recap is a
   * story somebody is telling you.
   */
  recap: {
    thanks:
      'Two days, one question, and a room full of women willing to sit with it. There were lines at the prayer tables both nights, notebooks full by Saturday afternoon, and more than a few friendships that started at a lunch table.',
    // Says what the weekend was about rather than how it felt. "Nobody wanted
    // to go home" is true of a good party; this is the conference's own
    // question, answered in four words, on a photograph of a woman doing
    // exactly that.
    headline: 'What matters is Him.',
    next: { label: "What's Next: Christmas", url: 'https://woodsidebible.org/christmas' },
    photos: [
      /**
       * The lead, and the one placeholder chosen for meaning rather than for
       * filling a frame: a woman with one hand up in a dark room, shot from
       * behind. Nothing about her is identifiable, which is the point — she
       * reads as any woman in that room, and what the picture is actually of is
       * the answer to the conference's own question. A photograph of women
       * laughing says the weekend was fun; this one says what it was for.
       */
      {
        src: 'https://images.unsplash.com/photo-1675099074138-92b303c1868c?w=2000&q=75',
        alt: 'A woman with one hand raised in worship in a dim sanctuary, seen from behind',
        caption: 'Friday night worship.',
        shape: 'wide',
      },
      {
        src: 'https://images.unsplash.com/photo-1516450360452-9312f5e86fc7?w=1600&q=75',
        alt: 'Hands raised in a dark room lit magenta and blue during worship',
        caption: 'Worship, Friday night.',
        shape: 'wide',
      },
      {
        src: 'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=1000&q=75',
        alt: 'Three hands holding coffee cups together over a wooden table',
        caption: 'Coffee before the first session, Saturday morning.',
        shape: 'square',
      },
      {
        src: 'https://images.unsplash.com/photo-1531206715517-5c0ba140b2b8?w=1000&q=75',
        alt: 'A circle of hands stacked together, seen from above',
        caption: 'Praying together, Saturday afternoon.',
        shape: 'tall',
      },
      {
        src: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?w=1600&q=75',
        alt: 'Two speakers on a stage in a wide room with round tables and screens',
        caption: 'Session one, Friday night.',
        shape: 'wide',
      },
      {
        src: 'https://images.unsplash.com/photo-1607748862156-7c548e7e98f4?w=1000&q=75',
        alt: 'Two women sitting close together, laughing',
        caption: 'Between breakouts.',
        shape: 'square',
      },
      {
        src: 'https://images.unsplash.com/photo-1543269865-cbf427effbad?w=1000&q=75',
        alt: 'Four people talking around a table with notebooks and coffee',
        caption: 'Sacred Stewardship, Room 201.',
        shape: 'square',
      },
      {
        src: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?w=1600&q=75',
        alt: 'An audience seated in a darkened auditorium, faces lit from the stage',
        caption: 'The last session, Saturday afternoon.',
        shape: 'wide',
      },
      {
        src: 'https://images.unsplash.com/photo-1517457373958-b7bdd4587205?w=1000&q=75',
        alt: 'A crowd of people outdoors in the evening under strings of lights',
        caption: 'The send-off.',
        shape: 'square',
      },
      {
        src: 'https://images.unsplash.com/photo-1511578314322-379afb476865?w=1000&q=75',
        alt: 'An empty room of round tables facing three blank screens',
        caption: 'The Worship Center, before the doors opened.',
        shape: 'square',
      },
    ],
    /**
     * Four questions, and the first one is the only one anybody has to answer.
     *
     * It comes up on the page rather than in a Google Form because the team is
     * already texting her a link to the recap — asking there means one tap and
     * no new tab, and it means the answers land next to the registration
     * instead of in a spreadsheet somebody has to reconcile later.
     */
    survey: {
      delaySeconds: 12,
      thanks: 'Thank you. Genuinely — this is how next year gets better.',
      questions: [
        { key: 'overall', prompt: 'How was the weekend for you?', kind: 'rating' },
        {
          key: 'favorite',
          prompt: 'Which breakout stayed with you?',
          kind: 'pickTopic',
          optional: true,
        },
        {
          key: 'again',
          prompt: 'Would you come again?',
          kind: 'yesNo',
          optional: true,
        },
        {
          key: 'words',
          prompt: 'Anything you want to tell the team?',
          hint: 'What you would change, what you would keep, what God did.',
          kind: 'text',
          optional: true,
        },
      ],
    },
  },

  /**
   * Next year, and the reason the recap has a shelf life.
   *
   * `teaserFrom` is the only switch: the day it passes, the front page stops
   * being November 2026 and starts being the next one, with the recap folded
   * into a look-back strip underneath. Dates below are a plausible placeholder
   * for the mock — the team sets the real ones, and the cadence (annual or
   * every other year) is theirs to confirm.
   */
  nextEdition: {
    label: '2028',
    teaserFrom: '2028-01-08T00:00:00-05:00',
    headline: 'We are gathering again.',
    dates: 'November 3 and 4, 2028',
    venue: 'Woodside Bible Church, Troy Campus',
    registrationOpens: '2028-05-08T09:00:00-04:00',
    note: 'The theme and the teacher come later this spring. The dates are the part you can put on a calendar today.',
  },
};
